import React, { useContext, useState } from "react";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Button, CircularProgress, Grid, TextField, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as Yup from "yup";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType, SERVER_URL } from "../../constants/global";
import { UNIQUE_USER } from "../../queries/pharmacy/pharmacyTypeDefs";
import { useMutationUpdateBankAccount } from "../../queries/bankAccount/hooks/useMutationUpdateBankAccount";
import { useAuth } from "../../hooks/useAuth";
import { CardBrandIcons } from "../Checkout/CardBrandIcons";

const STRIPE_ELEMENT_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#e53935", iconColor: "#e53935" },
  },
};

const stripeFieldFocusSx = {
  "&:focus-within": {
    borderColor: "primary.main",
    borderWidth: 2,
    p: "11px",
    minHeight: 48,
  },
};

const stripeInputBoxSx = {
  border: "1px solid",
  borderColor: "grey.400",
  borderRadius: 1,
  p: 1.5,
  minHeight: 48,
  width: "100%",
  boxSizing: "border-box",
  bgcolor: "background.paper",
  position: "relative",
  zIndex: 1,
  ...stripeFieldFocusSx,
};

const stripeElementHostSx = {
  width: "100%",
  minHeight: 24,
  lineHeight: 0,
  "& .StripeElement": {
    width: "100%",
  },
};

const stripeNumberBoxSx = {
  ...stripeInputBoxSx,
  display: "flex",
  alignItems: "center",
  gap: 1,
  pr: 1.5,
};

const fieldCaptionSx = { mb: 0.5, display: "block", minHeight: 18 };

interface ICardForm {
  clientSecret: string;
  bankAccountId: number;
  userId: number;
  pharmacy: {
    pharmacyId: number;
    Pharmacy?: { email?: string | null };
  };
  pharmacyUsersLoading: boolean;
  setIsEdit: (isEdit: boolean) => void;
  onCancel: () => void;
  onPaymentMethodSaved: () => Promise<void>;
  oldAccountId?: string | null;
}

type CardFormValues = {
  name: string;
  postalCode: string;
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name on card is required"),
  postalCode: Yup.string()
    .required("ZIP code is required")
    .matches(/^\d{5}(-\d{4})?$/, "Enter a valid US ZIP code"),
});

export const CardForm: React.FunctionComponent<ICardForm> = ({
  clientSecret,
  bankAccountId,
  userId,
  pharmacy,
  pharmacyUsersLoading,
  onCancel,
  onPaymentMethodSaved,
  oldAccountId,
}) => {
  const { addToast } = useContext(ToasterContext);
  const { token } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const pharmacyEmail = pharmacy?.Pharmacy?.email?.trim() || undefined;

  const [updateBankAccount, { loading }] = useMutationUpdateBankAccount({
    refetchQueries: [
      {
        query: UNIQUE_USER,
        variables: { where: { id: userId } },
        awaitRefetchQueries: true,
      },
    ],
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CardFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: "", postalCode: "" },
  });

  const detachOldPaymentMethod = (newPaymentMethodId: string) => {
    if (!oldAccountId || oldAccountId === newPaymentMethodId) return;
    fetch(`${SERVER_URL}/stripe/payment-method/${oldAccountId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {
      /* best-effort */
    });
  };

  const onSubmit = async (formData: CardFormValues) => {
    if (!stripe || !elements) return;

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      addToast({ type: NotificationType.Error, title: "Card form is not ready yet." });
      return;
    }

    setIsLoading(true);
    try {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name: formData.name.trim(),
          email: pharmacyEmail,
          address: {
            postal_code: formData.postalCode.trim(),
            country: "US",
          },
        },
      });
      if (pmError) {
        addToast({ type: NotificationType.Error, title: pmError.message || "Card was not accepted." });
        return;
      }

      if (paymentMethod?.card?.country && paymentMethod.card.country !== "US") {
        addToast({
          type: NotificationType.Error,
          title: "Only US-issued cards are accepted. Please use a different card.",
        });
        return;
      }

      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: paymentMethod.id,
      });
      if (setupError) {
        addToast({ type: NotificationType.Error, title: setupError.message || "Card setup failed." });
        return;
      }

      if (setupIntent && setupIntent.status === "succeeded") {
        const cardBrand = paymentMethod.card?.brand || "card";
        const cardLast4 = paymentMethod.card?.last4 || "";
        const requestData: any = {
          Pharmacy: { connect: { id: pharmacy.pharmacyId } },
          User: { connect: { id: userId } },
          accountHolderName: { set: formData.name.trim() },
          accountId: { set: paymentMethod.id },
          bankName: { set: cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) },
          country: { set: "United States" },
          currency: { set: "USD" },
          last4: { set: cardLast4 },
          object: { set: "card" },
          routingNumber: { set: null },
          status: { set: "VERIFIED" },
          token: { set: setupIntent.id },
          hostedVerificationUrl: { set: null },
        };

        await updateBankAccount({
          variables: { data: requestData, where: { id: bankAccountId } },
        });

        detachOldPaymentMethod(paymentMethod.id);

        addToast({ type: NotificationType.Success, title: "Card added successfully." });
        await onPaymentMethodSaved();
      } else {
        addToast({
          type: NotificationType.Error,
          title: "Card setup did not complete. Please try again.",
        });
      }
    } catch (err: any) {
      addToast({ type: NotificationType.Error, title: err?.message || "Card setup failed." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 4 }} elevation={0}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your US-issued card details below.
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
              Name on card
            </Typography>
            <Box
              sx={{
                ...stripeInputBoxSx,
                display: "flex",
                alignItems: "center",
                borderColor: errors.name ? "error.main" : "grey.400",
              }}
            >
              <TextField
                size="small"
                fullWidth
                required
                placeholder="Name on card"
                variant="standard"
                InputProps={{ disableUnderline: true }}
                inputProps={{ autoComplete: "cc-name" }}
                sx={{
                  m: 0,
                  "& .MuiInputBase-root": { fontSize: 16, py: 0 },
                  "& .MuiInputBase-input": { p: 0 },
                }}
                {...register("name")}
                error={!!errors.name}
              />
            </Box>
            {errors.name && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.name.message?.toString()}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
              Card number
            </Typography>
            <Box sx={stripeNumberBoxSx}>
              <Box sx={{ ...stripeElementHostSx, flex: 1, minWidth: 0 }}>
                <CardNumberElement
                  options={{
                    ...STRIPE_ELEMENT_STYLE,
                    showIcon: false,
                    placeholder: "Card number",
                  }}
                />
              </Box>
              <CardBrandIcons compact />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
              Expiration date
            </Typography>
            <Box sx={stripeInputBoxSx}>
              <Box sx={stripeElementHostSx}>
                <CardExpiryElement
                  options={{
                    ...STRIPE_ELEMENT_STYLE,
                    placeholder: "MM / YY",
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
              Security code
            </Typography>
            <Box sx={stripeInputBoxSx}>
              <Box sx={stripeElementHostSx}>
                <CardCvcElement
                  options={{
                    ...STRIPE_ELEMENT_STYLE,
                    placeholder: "CVC",
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="caption" color="text.secondary" sx={fieldCaptionSx}>
              ZIP code
            </Typography>
            <Box
              sx={{
                ...stripeInputBoxSx,
                display: "flex",
                alignItems: "center",
                borderColor: errors.postalCode ? "error.main" : "grey.400",
              }}
            >
              <TextField
                size="small"
                fullWidth
                required
                placeholder="12345"
                variant="standard"
                InputProps={{ disableUnderline: true }}
                inputProps={{ inputMode: "numeric", autoComplete: "postal-code" }}
                sx={{
                  m: 0,
                  "& .MuiInputBase-root": { fontSize: 16, py: 0 },
                  "& .MuiInputBase-input": { p: 0 },
                }}
                {...register("postalCode")}
                error={!!errors.postalCode}
              />
            </Box>
            {errors.postalCode && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {errors.postalCode.message?.toString()}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Box
          sx={{
            mt: 2.5,
            mb: 3,
            px: 2,
            py: 1.5,
            borderRadius: 1.5,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 22, color: "primary.main", flexShrink: 0, mt: 0.15 }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, lineHeight: 1.3 }}>
              Saved for future checkouts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Your card will be stored securely alongside any saved bank account. You can choose
              which method to use at checkout, and update or replace this card here anytime.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 0, gap: 2, flexWrap: "wrap" }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!stripe || isLoading || loading || pharmacyUsersLoading}
          >
            Save Card
            {(isLoading || loading || pharmacyUsersLoading) && (
              <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />
            )}
          </Button>
          <Button variant="outlined" size="large" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CardForm;
